import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createApplePayPayment as createApplePayPaymentApi } from "@/api/apple-pay/api";
import type {
  NuveiCreateApplePayPaymentOptions,
  NuveiCreateApplePayPaymentResult,
} from "@/api/apple-pay/applepay";
import type {
  ApmDepositConfirmRequest,
  PayPalDepositConfirmRequest,
  StartDepositTransaction,
} from "@/api/cashier";
import { EnumErrorKey } from "@/api/cashier";
import {
  startTransactionMutation,
  updateTransactionMutation,
} from "@/api/cashier/@tanstack/react-query.gen";
import type { GooglePayMerchantInfoJwt } from "@/api/google-pay";
import {
  getGooglePayMerchantInfoJwt,
  requestGooglePayPaymentData,
} from "@/api/google-pay";
import type { PayPalEnv, PayPalTokenizePayload } from "@/api/paypal";
import { collectBraintreeDeviceData } from "@/api/paypal";
import type {
  NuveiSafeChargeCreatePaymentOptions,
  NuveiSafeChargePaymentResult,
} from "@/api/safeCharge";
import { isNuveiSuccess } from "@/api/safeCharge";
import { useAccount } from "@/hooks/deposit/useAccount";
import { useAmountForm } from "@/hooks/deposit/useAmountForm";
import { useDynamicForm } from "@/hooks/deposit/useDynamicForm";
import { useMethod } from "@/hooks/deposit/useMethod";
import { useLogger } from "@/hooks/useLogger";
import { useUserData } from "@/hooks/useUserData";
import { reportNuveiSdkError } from "@/observability/nuvei";
import {
  getMerchantConfigs,
  toMajorUnits,
  toMinorUnits,
} from "@/utils/helpers";
import { serializeError } from "@/utils/log";

/**
 * Subset of PayPalDepositConfirmRequest for existing SPA payments (case 2).
 * The OpenAPI spec requires all fields, but the backend only needs device_data
 * when confirming a deposit with an already-vaulted PayPal account.
 * TODO: update the OpenAPI spec to model this as a union/discriminated type.
 */
type PayPalSpaConfirmApmFields = {
  paypal_request: Pick<PayPalDepositConfirmRequest, "device_data">;
};

/** APM methods that require start-transaction before showing the payment button (Next → session → button). */
const isApmDeferred = (method?: { id?: string }) =>
  method?.id === "applepay" || method?.id === "paypal";

// t("global.apm_payment_config_error")
const ERROR_KEY = "global.apm_payment_config_error";

/**
 * update-transaction error keys for which the backend has already persisted the
 * transaction as `failed` (unusable saved payment account → 422, PSP
 * update-session failure → 500). For these the customer should land on the
 * unsuccessful thank-you page — mirroring the behaviour before PNX-5837, when
 * the same failures returned HTTP 200 and the success path navigated there.
 */
const TRANSACTION_FAILED_ERROR_KEYS: string[] = [
  EnumErrorKey.DepositSavedPaymentAccountInvalidStatus,
  EnumErrorKey.DepositPspError,
];

/** Nuvei createPayment payload for Google Pay; mobileToken is paymentMethodData from Google. */
type GooglePayCreatePaymentRequest = {
  sessionToken: string;
  merchantId: string;
  merchantSiteId: string;
  env: string;
  clientUniqueId?: string;
  mobileToken: string;
  billingAddress?: { email: string; country: string };
};

type ApmStartTransactionContext = {
  processingAmount: number;
  tradingAmount: number;
  processingCurrencyCode: string;
};

const createGooglePayPaymentAsync = (
  request: GooglePayCreatePaymentRequest
): Promise<NuveiSafeChargePaymentResult> => {
  if (!request.mobileToken) {
    return Promise.reject(new Error("Google Pay mobileToken is required"));
  }

  const paymentRequest: NuveiSafeChargeCreatePaymentOptions = {
    sessionToken: request.sessionToken,
    merchantId: request.merchantId,
    merchantSiteId: request.merchantSiteId,
    env: request.env,
    clientUniqueId: request.clientUniqueId,
    billingAddress: request.billingAddress,
    paymentOption: {
      card: {
        externalToken: {
          externalTokenProvider: "GooglePay",
          mobileToken: request.mobileToken,
        },
      },
    },
    googlePay3Dflow: "enable",
  };

  return new Promise((resolve, reject) => {
    if (!window.safeChargeSdkInstance) {
      reject(new Error("SafeCharge SDK is not initialized"));
      return;
    }
    window.safeChargeSdkInstance.createPayment(paymentRequest, (response) => {
      if (isNuveiSuccess(response)) {
        resolve(response);
      } else {
        reportNuveiSdkError("createPayment", response);
        reject(new Error("Google Pay payment failed"));
      }
    });
  });
};

export type ApmMerchantConfig = {
  env: string;
  merchantId: string;
  merchantSiteId: string;
};

export type UseApmPaymentOptions = {
  /** Called when either Google Pay or Apple Pay payment succeeds (e.g. navigate to completion). */
  onPaymentSuccess?: (transactionId: string) => void;
  /**
   * Called when update-transaction reports the deposit as failed (the backend
   * has already persisted the `failed` status), e.g. navigate to the
   * unsuccessful thank-you page.
   */
  onPaymentFailure?: (transactionId: string) => void;
};

export const useApmPayment = (options: UseApmPaymentOptions = {}) => {
  const { getFormData } = useDynamicForm();
  const { onPaymentSuccess, onPaymentFailure } = options;
  const { t, i18n } = useTranslation();
  const onPaymentSuccessRef = useRef(onPaymentSuccess);
  onPaymentSuccessRef.current = onPaymentSuccess;
  const onPaymentFailureRef = useRef(onPaymentFailure);
  onPaymentFailureRef.current = onPaymentFailure;

  const [error, setError] = useState<string | null>(null);

  const { data: userData } = useUserData();
  const { selectedAccount } = useAccount();
  const { selectedPaymentMethod, selectedSavedPaymentAccount } = useMethod();
  const { amountFormData } = useAmountForm();
  const logger = useLogger();

  const paymentCurrencyCode = amountFormData
    ? (amountFormData?.selectedProcessingCurrency?.code ??
      selectedAccount?.currency.code)
    : selectedAccount?.currency.code;

  const paymentCurrencyDecimals = amountFormData
    ? (amountFormData?.selectedProcessingCurrency?.decimals ??
      selectedAccount?.currency.decimals)
    : selectedAccount?.currency.decimals;

  const {
    mutate: startTransaction,
    mutateAsync: startTransactionAsync,
    data: startTransactionResponse,
    isPending: isProcessingPayment,
  } = useMutation({
    ...startTransactionMutation(),
    onSuccess: () => {
      setError(null);
    },
    onError: (error) => {
      setError(error.key ?? error.message);
      setApmDeferredSession(null);
    },
  });

  /** Session from start-transaction for deferred APMs (Apple Pay, PayPal). Set when user clicks Next. */
  const [apmDeferredSession, setApmDeferredSession] = useState<{
    response: {
      session_token?: string;
      reference_id?: string;
      merchant_id?: string;
      merchant_site_id?: string;
      env?: string;
    };
    processingAmount: number;
    tradingAmount: number;
    processingCurrencyCode: string;
  } | null>(null);

  // Reset deferred session when method or amount changes
  useEffect(() => {
    if (!isApmDeferred(selectedPaymentMethod)) setApmDeferredSession(null);
    else if (
      apmDeferredSession &&
      (amountFormData?.processingValue !==
        apmDeferredSession.processingAmount ||
        amountFormData?.tradingValue !== apmDeferredSession.tradingAmount)
    ) {
      setApmDeferredSession(null);
    }
  }, [
    selectedPaymentMethod?.id,
    amountFormData?.processingValue,
    amountFormData?.tradingValue,
    apmDeferredSession,
  ]);

  const {
    mutateAsync: createPaymentAsync,
    error: createPaymentError,
    isPending: isGooglePayPaymentPending,
  } = useMutation({
    mutationFn: createGooglePayPaymentAsync,
    mutationKey: [
      "createGooglePayPayment",
      startTransactionResponse?.reference_id,
    ],
    onSuccess: (_data, variables) => {
      setError(null);
      const referenceId =
        variables.clientUniqueId ?? startTransactionResponse?.reference_id;
      if (referenceId) {
        onPaymentSuccessRef.current?.(referenceId);
      }
    },
    onError: (error) => {
      logger.error("[Google Pay] payment failed", {
        error: serializeError(error),
      });
      setError(t(ERROR_KEY));
    },
  });

  const {
    mutateAsync: createApplePayPaymentAsync,
    error: createApplePayPaymentError,
    isPending: isApplePayPaymentPending,
  } = useMutation({
    mutationFn: createApplePayPaymentApi,
    mutationKey: [
      "createApplePayPayment",
      startTransactionResponse?.reference_id,
    ],
    onSuccess: () => {
      setError(null);
      const referenceId = startTransactionResponse?.reference_id;
      if (referenceId) {
        onPaymentSuccessRef.current?.(referenceId);
      }
    },
    onError: (error) => {
      logger.error("[Apple Pay] payment failed", {
        error: serializeError(error),
      });
      setError(t(ERROR_KEY));
    },
  });

  const { mutate: updateTransaction, isPending: isPayPalPaymentPending } =
    useMutation({
      ...updateTransactionMutation(),
      mutationKey: [
        "updateTransactionPayPal",
        startTransactionResponse?.reference_id,
      ],
      onSuccess: () => {
        setError(null);
        const referenceId = startTransactionResponse?.reference_id;
        if (referenceId) {
          onPaymentSuccessRef.current?.(referenceId);
        }
      },
      onError: (error, variables) => {
        logger.error("[PayPal] payment failed", {
          error: serializeError(error),
        });

        // The transaction tied to this session is now failed on the backend —
        // reusing its reference_id on a retry would 409 (transaction_not_pending).
        // Drop the session so a retry starts a fresh transaction.
        setApmDeferredSession(null);

        // Business/provider failures (422 unusable SPA, 500 PSP error) have
        // already been persisted as `failed` — take the customer to the
        // unsuccessful thank-you page, as the pre-PNX-5837 HTTP-200 flow did.
        const referenceId = variables.path.referenceID;
        if (
          error.key &&
          TRANSACTION_FAILED_ERROR_KEYS.includes(error.key) &&
          referenceId
        ) {
          onPaymentFailureRef.current?.(referenceId);
          return;
        }

        setError(t(ERROR_KEY));
      },
    });

  const isPending =
    isProcessingPayment ||
    isGooglePayPaymentPending ||
    isApplePayPaymentPending ||
    isPayPalPaymentPending;

  const buildApmTransactionPayload = useCallback(
    (
      processingAmount: number,
      tradingAmount: number
    ): {
      payload: StartDepositTransaction;
      context: ApmStartTransactionContext;
    } => {
      if (!selectedPaymentMethod || !selectedAccount) {
        throw new Error("Missing payment context");
      }

      const fields = getFormData(selectedPaymentMethod.id) ?? {};
      const processingCurrencyDecimals =
        paymentCurrencyDecimals ?? selectedAccount.currency.decimals;
      const processingCurrencyCode =
        paymentCurrencyCode ?? selectedAccount.currency.code;
      const context: ApmStartTransactionContext = {
        processingAmount: toMajorUnits(
          toMinorUnits(processingAmount, processingCurrencyDecimals),
          processingCurrencyDecimals
        ),
        tradingAmount: toMajorUnits(
          toMinorUnits(tradingAmount, selectedAccount.currency.decimals),
          selectedAccount.currency.decimals
        ),
        processingCurrencyCode,
      };

      const payload: StartDepositTransaction = {
        payment_method_id: selectedPaymentMethod.id,
        trading_account_id: selectedAccount.id,
        trading_amount_with_currency: {
          amount: String(
            toMinorUnits(tradingAmount, selectedAccount.currency.decimals)
          ),
          code: selectedAccount.currency.code,
        },

        ...(selectedSavedPaymentAccount?.id && {
          saved_payment_account_id: selectedSavedPaymentAccount?.id,
        }),
        ...(!selectedSavedPaymentAccount?.id &&
          fields && {
            form_fields: fields,
          }),
        validate_spa_fields: true,
      };

      return { payload, context };
    },
    [
      getFormData,
      selectedPaymentMethod,
      selectedAccount,
      paymentCurrencyCode,
      paymentCurrencyDecimals,
      selectedSavedPaymentAccount?.id,
    ]
  );

  const handleApmDeferredNextClick = useCallback(() => {
    if (
      !isApmDeferred(selectedPaymentMethod) ||
      !userData ||
      !selectedAccount ||
      !amountFormData?.processingValue ||
      !amountFormData?.tradingValue
    )
      return;
    setError(null);
    const { payload, context } = buildApmTransactionPayload(
      amountFormData.processingValue,
      amountFormData.tradingValue
    );
    startTransaction(
      { body: payload },
      {
        onSuccess: (data) => {
          if (
            data &&
            isApmDeferred(selectedPaymentMethod) &&
            selectedAccount &&
            !selectedSavedPaymentAccount
          ) {
            setApmDeferredSession({ response: data, ...context });
          }
        },
      }
    );
  }, [
    selectedPaymentMethod,
    userData,
    selectedAccount,
    selectedSavedPaymentAccount,
    amountFormData,
    buildApmTransactionPayload,
    startTransaction,
  ]);

  const handleApplePayClick = useCallback((): Promise<void> => {
    if (
      !userData ||
      !selectedAccount ||
      !apmDeferredSession?.response?.session_token
    )
      return Promise.resolve();
    const { response, processingAmount, processingCurrencyCode } =
      apmDeferredSession;
    if (!response.reference_id) return Promise.resolve();
    const referenceID = response.reference_id;
    setError(null);
    const merchantConfigs = getMerchantConfigs(response);
    const nameParts = userData.name?.trim().split(/\s+/) ?? [];
    const firstName = nameParts[0] ?? "";
    const lastName = nameParts.slice(1).join(" ") ?? "";
    const options: NuveiCreateApplePayPaymentOptions = {
      sessionToken: response.session_token!,
      merchantId: merchantConfigs.merchantId,
      merchantSiteId: merchantConfigs.merchantSiteId,
      env: merchantConfigs.env,
      clientUniqueId: response.reference_id,
      billingAddress: {
        firstName,
        lastName,
        country: userData.country_code ?? "",
        email: userData.email ?? "",
      },
      userDetails: {
        firstName,
        lastName,
        country: userData.country_code ?? "",
        email: userData.email ?? "",
      },
      countryCode: userData.country_code ?? "GB",
      currencyCode: processingCurrencyCode,
      applePay3Dflow: "enable",
      collectUserDetails: false,
      total: { label: "Total", amount: processingAmount.toFixed(2) },
    };
    return createApplePayPaymentAsync(options).then((result) => {
      const { userPaymentOptionId } =
        result as NuveiCreateApplePayPaymentResult & {
          userPaymentOptionId: string;
        };
      return handleApplePayGooglePayResponse(referenceID, userPaymentOptionId);
    });
  }, [
    userData,
    apmDeferredSession,
    selectedAccount,
    createApplePayPaymentAsync,
  ]);

  const processPayPalPayment = useCallback(
    (payload: PayPalTokenizePayload) => {
      if (
        !startTransactionResponse?.reference_id ||
        !selectedAccount ||
        !amountFormData
      ) {
        return;
      }
      const { nonce, details, deviceData } = payload;
      if (!nonce) {
        logger.error("[PayPal] missing nonce in tokenize payload");
        setError(t("global.apm_payment_config_error"));
        return;
      }

      const isExistingSpa = Boolean(selectedSavedPaymentAccount);

      const payerId = details?.payerId ?? "";
      const payerEmail = details?.email ?? "";
      const firstName = details?.firstName ?? "";
      const lastName = details?.lastName ?? "";
      const address = details?.billingAddress ?? details?.shippingAddress;

      updateTransaction({
        path: { referenceID: startTransactionResponse.reference_id },
        body: {
          ...(!isExistingSpa && {
            form_fields: {
              first_name: firstName,
              last_name: lastName,
              email: payerEmail,
              payer_id: payerId,
            },
          }),
          apm_fields: isExistingSpa
            ? ({
                paypal_request: {
                  device_data: deviceData ?? "",
                },
              } as PayPalSpaConfirmApmFields as ApmDepositConfirmRequest)
            : {
                paypal_request: {
                  nonce,
                  device_data: deviceData ?? "",
                  payer_email: payerEmail,
                  first_name: firstName,
                  last_name: lastName,
                  payer_id: payerId,
                  phone: details?.phone ?? "",
                  country_code:
                    details?.countryCode ?? address?.countryCode ?? "",
                  address_line: address?.line1 ?? "",
                  city: address?.city ?? "",
                  state: address?.state ?? "",
                  postal_code: address?.postalCode ?? "",
                  billing_country_code:
                    address?.countryCode ?? details?.countryCode ?? "",
                },
              },
        },
      });
    },
    [
      startTransactionResponse,
      selectedAccount,
      amountFormData,
      selectedSavedPaymentAccount,
      updateTransaction,
      t,
    ]
  );

  const handlePayPalSpaPayment = useCallback(async () => {
    if (
      !selectedPaymentMethod ||
      !userData ||
      !selectedAccount ||
      !amountFormData?.processingValue ||
      !amountFormData?.tradingValue
    ) {
      return;
    }
    setError(null);
    try {
      const { payload } = buildApmTransactionPayload(
        amountFormData.processingValue,
        amountFormData.tradingValue
      );
      const response = await startTransactionAsync({ body: payload });
      if (!response?.session_token || !response?.reference_id) {
        logger.error(
          "[PayPal SPA] start-transaction response missing session_token or reference_id"
        );
        setError(t(ERROR_KEY));
        return;
      }

      const deviceData = await collectBraintreeDeviceData(
        response.session_token
      );

      updateTransaction({
        path: { referenceID: response.reference_id },
        body: {
          form_fields: {},
          apm_fields: {
            paypal_request: {
              device_data: deviceData,
            },
          } as PayPalSpaConfirmApmFields as ApmDepositConfirmRequest,
          saved_payment_account_id: selectedSavedPaymentAccount?.id,
        },
      });
    } catch (err) {
      logger.error("[PayPal SPA] payment failed", {
        error: serializeError(err),
      });
      setError(t(ERROR_KEY));
    }
  }, [
    selectedPaymentMethod,
    userData,
    selectedAccount,
    amountFormData,
    buildApmTransactionPayload,
    startTransactionAsync,
    updateTransaction,
    logger,
    t,
  ]);

  const handleApplePayGooglePayResponse = async (
    referenceID: string,
    userPaymentOptionId: string
  ) => {
    return updateTransaction({
      path: { referenceID },
      body: {
        psp_token: userPaymentOptionId,
      },
    });
  };

  const handleGooglePayClick = useCallback(async () => {
    if (
      !selectedPaymentMethod ||
      !userData ||
      !selectedAccount ||
      !amountFormData?.processingValue ||
      !amountFormData?.tradingValue
    ) {
      return;
    }
    setError(null);
    const { payload } = buildApmTransactionPayload(
      amountFormData.processingValue,
      amountFormData.tradingValue
    );
    const response = await startTransactionAsync({ body: payload });
    if (!response?.session_token) return;
    const merchantConfigs = getMerchantConfigs(response);
    const merchantConfig = {
      merchant_id: response.merchant_id ?? merchantConfigs.merchantId,
      merchant_site_id:
        response.merchant_site_id ?? merchantConfigs.merchantSiteId,
    };

    // Nuvei domain-verification (JWT) step: fetch the registered domain + JWT for
    // this session and attach it to the Google Pay request. Required once domains
    // are onboarded via Nuvei's JWT solution — without it Google Pay rejects the
    // domain, so a failure here must stop the flow.
    let merchantInfoJwt: GooglePayMerchantInfoJwt;
    try {
      merchantInfoJwt = await getGooglePayMerchantInfoJwt({
        sessionToken: response.session_token,
        merchantOrigin: window.location.hostname,
      });
    } catch (err) {
      logger.error("[Google Pay] merchant info JWT fetch failed", {
        error: serializeError(err),
      });
      setError(t(ERROR_KEY));
      return;
    }

    const paymentData = await requestGooglePayPaymentData(
      merchantConfig,
      {
        totalPrice: amountFormData.processingValue.toFixed(2),
        totalPriceStatus: "FINAL",
        currencyCode: paymentCurrencyCode ?? selectedAccount.currency.code,
      },
      merchantInfoJwt
    );
    const paymentMethodData = paymentData.paymentMethodData;
    if (!paymentMethodData) {
      logger.error(
        "[Google Pay] paymentMethodData absent in Google Pay response"
      );
      setError(t(ERROR_KEY));
      return;
    }
    const paymentResult = await createPaymentAsync({
      sessionToken: response.session_token,
      merchantId: merchantConfigs.merchantId,
      merchantSiteId: merchantConfigs.merchantSiteId,
      env: merchantConfigs.env,
      clientUniqueId: response.reference_id,
      mobileToken: JSON.stringify(paymentMethodData),
      billingAddress: {
        email: userData.email ?? "",
        country: userData.country_code ?? "",
      },
    });

    const { userPaymentOptionId } =
      paymentResult as NuveiSafeChargePaymentResult & {
        userPaymentOptionId: string;
      };

    await handleApplePayGooglePayResponse(
      response.reference_id,
      userPaymentOptionId
    );
  }, [
    selectedPaymentMethod,
    userData,
    selectedAccount,
    amountFormData,
    buildApmTransactionPayload,
    startTransactionAsync,
    createPaymentAsync,
    paymentCurrencyCode,
    logger,
    t,
  ]);

  const handleApmPaymentError = useCallback(
    (error?: Error) => {
      logger.error("[APM] payment button reported an error", {
        error: serializeError(error),
      });
      setError(t(ERROR_KEY));
    },
    [logger, t]
  );

  // Narrow the BE-provided env (typed as `string`) to PayPalEnv so consumers
  // don't have to validate it themselves. Anything unexpected becomes undefined,
  // letting renderPayPalButton fall back to the build-time env.
  const rawApmDeferredEnv = apmDeferredSession?.response?.env;
  const apmDeferredSessionEnv: PayPalEnv | undefined =
    rawApmDeferredEnv === "sandbox" || rawApmDeferredEnv === "production"
      ? rawApmDeferredEnv
      : undefined;

  return {
    handleApplePayClick,
    handleGooglePayClick,
    handleApmDeferredNextClick,
    handlePayPalSpaPayment,
    processPayPalPayment,
    apmDeferredReady: Boolean(apmDeferredSession),
    apmDeferredSessionToken:
      apmDeferredSession?.response?.session_token ?? null,
    apmDeferredSessionEnv,
    handleApmPaymentError,
    // update-transaction errors are handled in its onError: persisted failures
    // navigate to the unsuccessful TYP, everything else sets `error` — so only
    // the SDK createPayment errors need mapping here.
    apmPaymentError:
      createPaymentError || createApplePayPaymentError
        ? t(ERROR_KEY)
        : error
          ? i18n.exists(error)
            ? t(error)
            : t(ERROR_KEY)
          : null,
    isPending,
    isProcessingApmPayment: isProcessingPayment,
  };
};
